import { Box, Card, Divider, Stack, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { ReportAggregateBucket, ReportAggregatesFilters } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { payApiClient } from '@/api/client';
import { formatMinorUnits } from '@/utils/amount';
import { useApiResource } from '@/hooks/useApiResource';
import { capitalizeValue } from './domain';

interface AggregatesViewProps {
  filters: ReportAggregatesFilters;
}

function tenantPart(bucket: ReportAggregateBucket): string | undefined {
  if (!bucket.tenantCode) return undefined;
  return bucket.tenantName ? `${bucket.tenantName} (${bucket.tenantCode})` : bucket.tenantCode;
}

function bucketLabel(bucket: ReportAggregateBucket, translate: (key: string) => string): string {
  const parts = [
    bucket.applicationCode,
    tenantPart(bucket),
    bucket.gateway ? capitalizeValue(bucket.gateway) : undefined,
    bucket.period,
    // Present only under `groupBy=tag`; `null` there is the untagged bucket.
    bucket.tag !== undefined ? (bucket.tag ?? translate('pay.statistics.breakdown.untagged')) : undefined,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : translate('pay.operations.aggregates.bucketUnlabelled');
}

/** Formats a contract fraction (e.g. `0.9405`) as a percentage string. Never converts the underlying amount fields. */
function formatRate(rate: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(rate);
}

export function AggregatesView({ filters }: AggregatesViewProps) {
  const translate = useTranslate();
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.getReportAggregates(filters),
    JSON.stringify(filters),
  );

  if (loading) {
    return (
      <Card>
        <LoadingState variant="card" />
      </Card>
    );
  }

  if (error) {
    if (error.errorCode === 'tenant_not_authorized') {
      return (
        <Card>
          <AccessDenied body={error.message} />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.operations.list.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  if (!data || data.buckets.length === 0) {
    return (
      <Card>
        <EmptyState
          title={translate('pay.operations.aggregates.emptyTitle')}
          body={translate('pay.operations.aggregates.emptyBody')}
        />
      </Card>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 2,
      }}
    >
      {data.buckets.map((bucket, index) => (
        <Card key={index} sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {bucketLabel(bucket, translate)}
            </Typography>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {translate('pay.operations.aggregates.fieldTotalCount')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono }}>
                {bucket.totalCount}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {translate('pay.operations.aggregates.fieldSuccessCount')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono }}>
                {bucket.successCount}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {translate('pay.operations.aggregates.fieldSuccessRate')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono }}>
                {formatRate(bucket.successRate)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {translate('pay.operations.aggregates.fieldAbandonmentRate')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono }}>
                {formatRate(bucket.abandonmentRate)}
              </Typography>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {translate('pay.operations.aggregates.fieldTotalAmount')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono, fontWeight: 600 }}>
                {formatMinorUnits(bucket.totalAmount, bucket.currency)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {translate('pay.operations.aggregates.fieldRefundedAmount')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono }}>
                {formatMinorUnits(bucket.refundedAmount, bucket.currency)}
              </Typography>
            </Stack>
          </Stack>
        </Card>
      ))}
    </Box>
  );
}
